// Field 화면들의 **배지 정본** — 고객사 리포트 · 고장 조치 이력 · 로봇 상세의
// '고장 이력' 탭이 같이 쓴다.
//
// 한 곳에 모은 이유: 같은 상태가 화면마다 다른 색이면 읽는 사람이 규칙을 다시
// 배워야 하고, 한쪽만 고치는 사고가 난다(이 저장소가 실제로 겪은 사고의 원인).
//
// 색은 전부 토큰이다. hex 를 박으면 다크 모드에서 배경에 묻고 브랜드 전환을
// 따라오지 않는다. tint 배경 위 글자는 `--fg-on-*-tint` 짝을 쓴다.

import type { CSSProperties } from 'react';
import type { IconName } from '@/components/Icon';
import { RemediationStatus, type ServiceOrderStatus, type ServiceSeverity } from '@bhops/shared';

/** 배지 공통 모양 — 등급·상태가 같은 문법으로 보이게 한 곳에서 잡는다. */
export const pill: CSSProperties = {
  borderRadius: 'var(--r-pill)',
  padding: '2px 8px',
  fontSize: 11,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  display: 'inline-block',
};

export interface Tone {
  fg: string;
  bg: string;
  /** 표 왼쪽 색 띠. 배지보다 진한 전경색을 쓴다. */
  bar: string;
}

/** 등급 P1/P2/P3. */
export const SEVERITY_TONE: Record<ServiceSeverity, Tone> = {
  P1: { fg: 'var(--fg-danger)', bg: 'var(--bg-danger-subtle)', bar: 'var(--fg-danger)' },
  P2: {
    fg: 'var(--fg-on-warning-tint)',
    bg: 'var(--bg-warning-subtle)',
    bar: 'var(--dday-warning)',
  },
  P3: {
    fg: 'var(--fg-on-success-tint)',
    bg: 'var(--bg-success-subtle)',
    // ⚠ `--border-success-saturated` 는 이름과 달리 **테두리용 연한 색**이다.
    //    띠에 쓰면 P1·P2 옆에서 혼자 바래 보인다(2026-09-14 실측 후 교정).
    bar: 'var(--robot-operating)',
  },
};

export const SEVERITY_ORDER: ServiceSeverity[] = ['P1', 'P2', 'P3'];

/** 등급 카드 이름 앞 아이콘(2026-10-28 — 대시보드 카드 모양). */
export const SEVERITY_ICON: Record<ServiceSeverity, IconName> = {
  P1: 'siren',
  P2: 'alert',
  P3: 'inbox',
};

/**
 * 고객사 리포트의 상태 — 화면이 쓰는 4종만 색을 준다.
 *
 * 그 밖의 값(과거 데이터의 '종결' 등)은 `SHEET_STATUS_TONE_UNKNOWN` 으로 떨어진다.
 * 뜻을 모르는 색을 지어내면 읽는 사람이 규칙을 잘못 배운다.
 */
export const SHEET_STATUS_TONE: Partial<Record<ServiceOrderStatus, CSSProperties>> = {
  BLOCKED: { color: 'var(--fg-on-warning-tint)', background: 'var(--bg-warning-subtle)' }, // 대기
  IN_PROGRESS: {
    color: 'var(--fg-on-success-tint)',
    background: 'var(--bg-success-subtle)',
  }, // 진행중
  RESOLVED: { color: 'var(--robot-shipping)', background: 'var(--robot-shipping-tint)' }, // 해결
  CANCELLED: { color: 'var(--fg-on-danger-tint)', background: 'var(--bg-danger-subtle)' }, // 취소
};

export const SHEET_STATUS_TONE_UNKNOWN: CSSProperties = {
  color: 'var(--fg-muted)',
  background: 'var(--bg-subtle)',
};

/**
 * 고장 조치 이력의 상태.
 *
 * | 상태          | 색   | 왜 |
 * | ------------- | ---- | -- |
 * | 재방문 필요   | 빨강 | 아직 가야 한다 |
 * | 조치 진행 중  | 초록 | 지금 붙어서 하는 중 |
 * | 모니터링      | 앰버 | 지켜보는 중 |
 * | 해결 완료     | 파랑 | 끝났다 |
 *
 * **고객사 리포트와 같은 색을 쓴다**(오너 결정 2026-09-16). 그쪽은 이미
 * 진행중=초록 · 해결=파랑이고, 조치 이력의 상태는 신고 상태로 전파된다
 * (`reportStatusForRemediation`: 해결 완료 → 해결, 나머지 → 진행중).
 * 두 표에 같은 건이 나란히 놓이는데 색이 어긋나면, 전파가 제대로 됐는지를
 * 색으로 읽을 수 없다.
 *
 * 종전에는 반대였다(진행 중=파랑, 완료=초록). "끝난 것은 초록" 이라는 흔한
 * 직관을 따랐는데, 그러면 리포트 쪽 '해결'(파랑)과 정면으로 어긋났다.
 */
export const REMEDIATION_TONE: Record<RemediationStatus, Tone> = {
  REVISIT_NEEDED: {
    fg: 'var(--fg-on-danger-tint)',
    bg: 'var(--bg-danger-subtle)',
    bar: 'var(--fg-danger)',
  },
  // 초록 — 고객사 리포트의 '진행중' 과 같은 토큰.
  IN_PROGRESS: {
    fg: 'var(--fg-on-success-tint)',
    bg: 'var(--bg-success-subtle)',
    bar: 'var(--robot-operating)',
  },
  // 보라 — 조치 후 원인 파악 중(2026-10-28). 진행 중(초록) · 모니터링(주황)과 섞이지 않게.
  OBSERVING: {
    fg: 'var(--robot-maintenance)',
    bg: 'var(--robot-maintenance-tint)',
    bar: 'var(--robot-maintenance)',
  },
  MONITORING: {
    fg: 'var(--fg-on-warning-tint)',
    bg: 'var(--bg-warning-subtle)',
    bar: 'var(--dday-warning)',
  },
  // 파랑 — 고객사 리포트의 '해결' 과 같은 토큰.
  COMPLETED: {
    fg: 'var(--robot-shipping)',
    bg: 'var(--robot-shipping-tint)',
    bar: 'var(--robot-shipping)',
  },
};

/** 카드·드롭다운 순서 — "손이 더 가야 하는 것"이 앞에. 끝난 건이 맨 뒤. */
export const REMEDIATION_ORDER: RemediationStatus[] = [
  RemediationStatus.REVISIT_NEEDED,
  RemediationStatus.IN_PROGRESS,
  RemediationStatus.OBSERVING,
  RemediationStatus.MONITORING,
  RemediationStatus.COMPLETED,
];

/** 상태 카드 이름 앞 아이콘(2026-10-28 — 대시보드 카드 모양). */
export const REMEDIATION_ICON: Record<RemediationStatus, IconName> = {
  REVISIT_NEEDED: 'refresh',
  IN_PROGRESS: 'wrench',
  OBSERVING: 'search',
  MONITORING: 'eye',
  COMPLETED: 'check-circle',
};
